import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "gatsby";
import React from "react";
import styled from "styled-components";
import tw from "twin.macro";

import dashboardSrc from "../../assets/images/integrations/dashboard.png";
import psLogoSrc from "../../assets/images/logo.svg";
import LayoutDefault from "../../layouts/LayoutDefault";
import SmoothScroll from "../elements/SmoothScroll";
import Hero, {
  HeroHeadline,
  HeroSplitLeft,
  HeroSplitRight,
  HeroSubheadline,
} from "../hero/Hero";
import Button from "./Button";
import Container from "./Container";
import FooterCta from "./FooterCta";
import HowItWorksSteps from "./HowItWorksSteps";
import Image from "./Image";
import Screenshot from "./Screenshot";
import Section, {
  SectionColor,
  SectionContent,
  SectionHeaderNoSubheadline,
  SectionHeadline,
} from "./Section";
import SEO from "./SEO";
import {
  SplitContent,
  SplitDescription,
  SplitHeadlineBlack,
  SplitImage,
  SplitItem,
  SplitItems,
} from "./SplitItems";

export interface ExtraFeature {
  headline: string;
  description: string;
  image: React.ReactNode;
}

interface IntegrationProps {
  seoTitle: string;
  seoDescription: string;
  title: string;
  description: React.ReactNode;
  logo: React.ReactNode;
  howItWorksDescription: string;
  howItWorksImage: React.ReactNode;
  features: string[];
  extraFeatures?: ExtraFeature[];
  aboveHero?: React.ReactNode;
}

const Breadcrumb = styled.div`
  ${tw`mb-[10px]`}

  a {
    ${tw`text-theme-blue-1 text-[24px] font-normal hover:underline!`}
  }
`;

const PlusProsperStack = styled.div`
  ${tw`flex flex-col justify-center items-center`}
  gap: 27px;
`;

const Plus = styled.div`
  ${tw`flex items-center justify-center text-theme-blue-1 font-bold text-center`}

  div {
    ${tw`bg-theme-blue-1 text-white text-center rounded-full`}
    height: 30px;
    width: 30px;
    font-size: 36px;
  }
`;

const IntegrationDetail: React.FunctionComponent<IntegrationProps> = ({
  seoTitle,
  seoDescription,
  title,
  description,
  logo,
  howItWorksDescription,
  howItWorksImage,
  features,
  extraFeatures = [],
  aboveHero,
}) => (
  <LayoutDefault>
    <SEO title={seoTitle} description={seoDescription} />
    <Hero>
      <HeroSplitLeft fullWidth={580}>
        <Breadcrumb>
          <Link to="/integrations/">Integrations</Link>
        </Breadcrumb>
        <HeroHeadline>{title}</HeroHeadline>
        <HeroSubheadline>{description}</HeroSubheadline>

        <SmoothScroll to="integration-features">
          <Button
            color="primary button-white"
            wideMobile
            href="#integration-features"
          >
            <span>
              Integration features <FontAwesomeIcon icon={faArrowDown} />
            </span>
          </Button>
        </SmoothScroll>
      </HeroSplitLeft>
      <HeroSplitRight tw="self-center">
        <PlusProsperStack>
          <div>{logo}</div>
          <Plus>
            <div>+</div>
          </Plus>
          <div>
            <Image src={psLogoSrc} alt="" style={{ maxWidth: "300px" }} />
          </div>
        </PlusProsperStack>
      </HeroSplitRight>
    </Hero>

    {aboveHero}

    <Container>
      <Section color={SectionColor.Gray}>
        <SectionHeaderNoSubheadline>
          <SectionHeadline>How ProsperStack works</SectionHeadline>
        </SectionHeaderNoSubheadline>
        <SectionContent>
          <HowItWorksSteps />

          <SplitItems tw="mt-[40px] lg:gap-[40px]">
            <SplitItem id="integration-features" tw="scroll-m-24">
              <SplitContent>
                <SplitHeadlineBlack>
                  How it works with {title}
                </SplitHeadlineBlack>
                <SplitDescription>
                  <p>{howItWorksDescription}</p>
                </SplitDescription>
              </SplitContent>
              <SplitImage>{howItWorksImage}</SplitImage>
            </SplitItem>

            <SplitItem>
              <SplitContent>
                <SplitHeadlineBlack>
                  {title} + ProsperStack features
                </SplitHeadlineBlack>
                <SplitDescription>
                  <ul>
                    {features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </SplitDescription>
              </SplitContent>
              <SplitImage even={true}>
                <Screenshot
                  src={dashboardSrc}
                  alt=""
                  width={576.5}
                  height={400}
                  style={{ width: "100%", maxWidth: "530px" }}
                />
              </SplitImage>
            </SplitItem>

            {extraFeatures.map((feature, i) => (
              <SplitItem key={feature.headline}>
                <SplitContent>
                  <SplitHeadlineBlack>{feature.headline}</SplitHeadlineBlack>
                  <SplitDescription>
                    <p>{feature.description}</p>
                  </SplitDescription>
                </SplitContent>
                <SplitImage even={i % 2 !== 0}>{feature.image}</SplitImage>
              </SplitItem>
            ))}
          </SplitItems>
        </SectionContent>
      </Section>
    </Container>

    <FooterCta />
  </LayoutDefault>
);

export default IntegrationDetail;
